<template>
    <AppLayout title="Report Generation">
        <LoadingOverlay v-model="exporting" title="Exporting…"
            message="Your download will start shortly. Click to dismiss." :autoCloseOnFocus="true"
            :fallbackMs="45000" />
        <div class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 space-y-6">
            <div class="flex items-center justify-between">
                <div>
                    <h1 class="text-xl font-semibold text-gray-900">Report Generation</h1>
                    <p class="text-sm text-gray-500">
                        Filter projects and export the results to a report.
                    </p>
                </div>

                <div class="flex gap-2">
                    <PrimaryButton type="button" @click="exportExcel" :disabled="exporting">
                        Export Report
                    </PrimaryButton>
                </div>
            </div>

            <div class="bg-white shadow rounded-lg p-4">
                <div v-if="validationError"
                    class="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {{ validationError }}
                </div>

                <form @submit.prevent="submitSearch" class="space-y-4">
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <InputLabel for="fromDate" value="From Date" />
                            <input id="fromDate" type="date"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm"
                                v-model="form.fromDate" />
                        </div>

                        <div>
                            <InputLabel for="toDate" value="To Date" />
                            <input id="toDate" type="date"
                                class="mt-1 block w-full border-gray-300 rounded-md shadow-sm" v-model="form.toDate" />
                        </div>

                        <div>
                            <InputLabel for="cid" value="Client" />
                            <SearchSelect v-model="cidModel" :options="clientOptions"
                                placeholder="Type to search clients…" filterMode="includes" />
                        </div>

                        <div>
                            <InputLabel for="vendorId" value="Vendor" />
                            <SearchSelect v-model="vendorIdModel" :options="vendorOptions"
                                placeholder="Type to search vendors…" filterMode="includes" />
                        </div>

                        <div>
                            <InputLabel for="manager_id" value="Project Manager" />
                            <SelectInput id="manager_id" class="mt-1"
                                v-model="form.manager_id">
                                <option value="">-----Select------</option>
                                <option v-for="pm in projectManagers" :key="pm.employeeID"
                                    :value="String(pm.employeeID)">
                                    {{ pm.firstname }} {{ pm.lastname }}
                                </option>
                            </SelectInput>
                        </div>

                        <div>
                            <InputLabel for="projectname" value="Project Name" />
                            <SearchSelect v-model="projectnameModel" :options="projectOptions"
                                placeholder="Type to search projects…" filterMode="includes" />
                        </div>

                        <div>
                            <InputLabel for="style" value="Style" />
                            <SearchSelect v-model="styleModel" :options="styleOptions"
                                placeholder="Type to search styles…" filterMode="includes" :allowFreeText="true" />
                        </div>

                        <div>
                            <InputLabel for="color" value="Color" />
                            <SearchSelect
                                v-if="usesSelectableColorField"
                                v-model="colorModel"
                                :options="colorOptions"
                                :placeholder="colorPlaceholder"
                                filterMode="includes"
                            />
                            <TextInput v-else id="color" class="mt-1 block w-full" v-model="form.color" />
                        </div>

                        <div>
                            <InputLabel for="purchase" value="Purchase Order" />
                            <TextInput id="purchase" class="mt-1 block w-full" v-model="form.purchase" />
                        </div>

                        <div>
                            <InputLabel for="invoice" value="Purchase Invoice" />
                            <TextInput id="invoice" class="mt-1 block w-full" v-model="form.invoice" />
                        </div>

                        <div>
                            <InputLabel for="sampleNmbr" value="Sample Number" />
                            <SelectInput id="sampleNmbr" class="mt-1"
                                v-model="form.sampleNmbr">
                                <option value="">---Select---</option>
                                <option v-for="s in samples" :key="s.id" :value="String(s.id)">
                                    {{ s.sample_id }}
                                </option>
                            </SelectInput>
                        </div>

                        <div>
                            <InputLabel for="notes" value="Notes" />
                            <TextInput id="notes" class="mt-1 block w-full" v-model="form.notes" />
                        </div>
                    </div>

                    <div class="border-t pt-4">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <div class="font-semibold text-gray-900 mb-2">
                                    Report Options
                                </div>

                                <label class="flex items-center gap-2">
                                    <input type="checkbox" v-model="form.closed_purchase" />
                                    <span class="text-sm text-gray-700">
                                        Closed Purchase Orders
                                    </span>
                                </label>

                                <label class="flex items-center gap-2 mt-2">
                                    <input type="checkbox" v-model="form.past_due_purchase" />
                                    <span class="text-sm text-gray-700">
                                        Past Due Purchase Orders
                                    </span>
                                </label>
                            </div>

                            <div class="flex items-end justify-end gap-2">
                                <SecondaryButton type="button" @click="resetFilters">
                                    Reset
                                </SecondaryButton>

                                <PrimaryButton type="submit" :disabled="loading">
                                    {{ loading ? "Searching…" : "Search" }}
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            <div class="bg-white shadow rounded-lg overflow-hidden">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                    Client
                                </th>
                                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                    Project Name
                                </th>
                                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                    PO
                                </th>
                                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                    PO Due Date
                                </th>
                                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                    Invoice
                                </th>
                                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                    Style
                                </th>
                                <th class="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                                    Color
                                </th>
                            </tr>
                        </thead>

                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-if="!hasSearched">
                                <td class="px-4 py-6 text-sm text-gray-600" colspan="7">
                                    Use the filters and click <b>Search</b> to generate results.
                                </td>
                            </tr>

                            <tr v-else-if="hasSearched && !rows?.data?.length">
                                <td class="px-4 py-6 text-sm text-gray-600" colspan="7">
                                    No projects found.
                                </td>
                            </tr>

                            <tr v-for="r in rows?.data ?? []" :key="`${r.pid}-${r.purchaseorder ?? ''}`">
                                <td class="px-4 py-3 text-sm text-gray-900">
                                    {{ r.client }}
                                </td>

                                <td class="px-4 py-3 text-sm">
                                    <a class="text-indigo-600 hover:text-indigo-800 underline" :href="String(r.is_closed) === '1'
                                        ? `/admin/projects/${r.pid}/closed`
                                        : route('admin.projects.edit', { project: r.pid })" target="_blank"
                                        rel="noopener noreferrer">
                                        {{ r.projectname }}
                                    </a>
                                    <div class="text-xs text-gray-500">PID: {{ r.pid }}</div>
                                </td>

                                <td class="px-4 py-3 text-sm text-gray-900">
                                    {{ r.purchaseorder || "" }}
                                </td>

                                <td class="px-4 py-3 text-sm whitespace-nowrap" :class="isPastDue(r.purchaseduedate)
                                    ? 'text-red-600 font-semibold'
                                    : 'text-gray-900'">
                                    {{ formatUsDate(r.purchaseduedate) || "" }}
                                </td>

                                <td class="px-4 py-3 text-sm text-gray-900">
                                    {{ r.pt_invoice || "" }}
                                </td>

                                <td class="px-4 py-3 text-sm text-gray-900">
                                    {{ r.style || "" }}
                                </td>

                                <td class="px-4 py-3 text-sm text-gray-900">
                                    {{ r.color || "" }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div class="p-4 border-t" v-if="rows?.links?.length">
                    <PaginationLinks :pagination="rows" />
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import { Inertia } from "@inertiajs/inertia"

import AppLayout from "@/Layouts/AppLayout.vue"
import InputLabel from "@/Components/InputLabel.vue"
import TextInput from "@/Components/TextInput.vue"
import SelectInput from "@/Components/SelectInput.vue"
import PrimaryButton from "@/Components/PrimaryButton.vue"
import SecondaryButton from "@/Components/SecondaryButton.vue"
import PaginationLinks from "@/Components/PaginationLinks.vue"
import SearchSelect from "@/Components/SearchSelect.vue"
import LoadingOverlay from "@/Components/ExportLoadingOverlay.vue"

const exporting = ref(false)

const props = defineProps({
    filters: { type: Object, default: () => ({}) },
    rows: { type: Object, default: null },

    clients: { type: Array, default: () => [] },
    vendors: { type: Array, default: () => [] },
    projectManagers: { type: Array, default: () => [] },
    samples: { type: Array, default: () => [] },

    styles: { type: Array, default: () => [] },

    projects: { type: Array, default: () => [] },
    colorField: { type: Object, default: () => ({ mode: "text", options: [] }) },
})

const toFilterString = (value) => (
    value == null || value === '' ? '' : String(value)
);

const clientOptions = computed(() =>
    (props.clients || [])
        .filter((client) => client?.client && (client.ID != null || client.id != null))
        .map((client) => ({
            label: String(client.client ?? ''),
            value: String(client.ID ?? client.id ?? ''),
        }))
        .filter((option) => option.label && option.value)
)

const vendorOptions = computed(() =>
    (props.vendors || [])
        .filter((vendor) => vendor && (vendor.vendorID != null || vendor.id != null))
        .map((vendor) => ({
            label: String(vendor.vendorName ?? vendor.name ?? ''),
            value: String(vendor.vendorID ?? vendor.id ?? ''),
        }))
        .filter((option) => option.label && option.value)
)

const styleOptions = computed(() =>
    (props.styles || []).map(s => ({
        label: String(s),
        value: String(s),
    }))
)

const projectOptions = computed(() => {
    const managerId = String(form.value.manager_id || "").trim()

    return (props.projects || [])
        .filter(p => {
            if (!managerId) return true
            return String(p.project_manager || "") === managerId
        })
        .map(p => ({
            label: String(p.projectname ?? ""),
            value: String(p.projectname ?? ""),
        }))
})

const usesGarmentColorField = computed(() => props.colorField?.mode === "garment_ids")
const usesSelectableColorField = computed(() =>
    props.colorField?.mode === "garment_ids" || props.colorField?.mode === "color_ids"
)

const colorOptions = computed(() =>
    (props.colorField?.options || []).map(option => ({
        label: String(option?.label ?? ""),
        value: String(option?.value ?? ""),
    })).filter(option => option.label && option.value)
)

const colorPlaceholder = computed(() =>
    usesGarmentColorField.value ? "Type to search garments…" : "Type to search colors…"
)

const form = ref({
    fromDate: toFilterString(props.filters.fromDate),
    toDate: toFilterString(props.filters.toDate),

    cid: toFilterString(props.filters.cid),
    vendorId: toFilterString(props.filters.vendorId),
    manager_id: toFilterString(props.filters.manager_id),

    projectname: toFilterString(props.filters.projectname),

    style: toFilterString(props.filters.style),

    color: toFilterString(props.filters.color),
    purchase: toFilterString(props.filters.purchase),
    invoice: toFilterString(props.filters.invoice),
    sampleNmbr: toFilterString(props.filters.sampleNmbr),
    notes: toFilterString(props.filters.notes),

    closed_purchase: props.filters.closed_purchase === undefined
        ? true
        : !!props.filters.closed_purchase,
    past_due_purchase: !!props.filters.past_due_purchase,
})

const cidModel = computed({
    get: () => (form.value.cid ? String(form.value.cid) : null),
    set: (value) => { form.value.cid = value == null ? '' : String(value) },
})

const vendorIdModel = computed({
    get: () => (form.value.vendorId ? String(form.value.vendorId) : null),
    set: (value) => { form.value.vendorId = value == null ? '' : String(value) },
})

const projectnameModel = computed({
    get: () => (form.value.projectname ? String(form.value.projectname) : null),
    set: (value) => { form.value.projectname = value == null ? '' : String(value) },
})

const styleModel = computed({
    get: () => (form.value.style ? String(form.value.style) : null),
    set: (value) => { form.value.style = value == null ? '' : String(value) },
})

const colorModel = computed({
    get: () => (form.value.color ? String(form.value.color) : null),
    set: (value) => { form.value.color = value == null ? '' : String(value) },
})

const loading = ref(false)

const validationError = ref("")

function hasAtLeastOneFilter(obj) {
    const ignoreSet = new Set(["closed_purchase", "past_due_purchase"])

    for (const [k, v] of Object.entries(obj || {})) {
        if (ignoreSet.has(k)) continue

        if (v === null || v === undefined) continue

        if (typeof v === "string") {
            if (v.trim() !== "") return true
            continue
        }

        if (typeof v === "boolean") {
            if (v === true) return true
            continue
        }

        if (Array.isArray(v)) {
            if (v.some(x => String(x ?? "").trim() !== "")) return true
            continue
        }

        if (String(v).trim() !== "") return true
    }

    return false
}

const hasSearched = computed(() => props.rows !== null)

watch(
    () => props.filters,
    (filters) => {
        hasSearched.value = Object.keys(filters || {}).length > 0
        form.value.fromDate = toFilterString(filters?.fromDate)
        form.value.toDate = toFilterString(filters?.toDate)
        form.value.cid = toFilterString(filters?.cid)
        form.value.vendorId = toFilterString(filters?.vendorId)
        form.value.manager_id = toFilterString(filters?.manager_id)
        form.value.projectname = toFilterString(filters?.projectname)
        form.value.style = toFilterString(filters?.style)
        form.value.color = toFilterString(filters?.color)
        form.value.purchase = toFilterString(filters?.purchase)
        form.value.invoice = toFilterString(filters?.invoice)
        form.value.sampleNmbr = toFilterString(filters?.sampleNmbr)
        form.value.notes = toFilterString(filters?.notes)
        form.value.closed_purchase = filters?.closed_purchase === undefined
            ? true
            : !!filters?.closed_purchase
        form.value.past_due_purchase = !!filters?.past_due_purchase
    },
    { deep: true }
)

watch(
    () => form.value.manager_id,
    () => {
        if (!form.value.projectname) return
        const allowed = projectOptions.value.some(o => o.value === form.value.projectname)
        if (!allowed) form.value.projectname = ""
    }
)

watch(
    () => form.value,
    () => { validationError.value = "" },
    { deep: true }
)

function cleanParams(obj) {
    const out = {}
    for (const [k, v] of Object.entries(obj)) {
        if (v === null || v === undefined) continue
        if (typeof v === "string" && v.trim() === "") continue
        if (typeof v === "boolean") {
            if (v) out[k] = 1
            continue
        }
        out[k] = v
    }
    return out
}

const submitSearch = () => {
    validationError.value = ""

    /*if (!hasAtLeastOneFilter(form.value)) {
        validationError.value = "You must select at least one filter."
        return
    }*/

    const params = cleanParams(form.value)
    loading.value = true

    Inertia.get(route("admin.reports.project-report.search"), params, {
        preserveScroll: true,
        preserveState: true,
        onFinish: () => (loading.value = false),
    })
}

const resetFilters = () => {
    validationError.value = ""

    form.value = {
        fromDate: "",
        toDate: "",

        cid: "",
        vendorId: "",
        manager_id: "",

        projectname: "",
        style: "",

        color: "",
        purchase: "",
        invoice: "",
        sampleNmbr: "",
        notes: "",

        closed_purchase: true,
        past_due_purchase: false,
    }

    Inertia.get(route("admin.reports.project-report.index"))
}

const exportExcel = () => {
    validationError.value = ""
    const params = cleanParams(form.value)
    exporting.value = true
    window.location.href = route("admin.reports.project-report.export", params)
}

function isPastDue(dateStr) {
    if (!dateStr) return false
    const d = new Date(dateStr)
    if (Number.isNaN(d.getTime())) return false

    const today = new Date()
    today.setHours(0, 0, 0, 0)
    d.setHours(0, 0, 0, 0)

    return d < today
}

function formatUsDate(value) {
    if (!value) return ""

    let date

    if (/^\d+$/.test(String(value))) {
        const ts = Number(value)
        date = ts < 1e12 ? new Date(ts * 1000) : new Date(ts)
    } else {
        date = new Date(value)
    }

    if (Number.isNaN(date.getTime())) return ""

    const mm = String(date.getMonth() + 1).padStart(2, "0")
    const dd = String(date.getDate()).padStart(2, "0")
    const yyyy = date.getFullYear()

    return `${mm}-${dd}-${yyyy}`
}
</script>
